import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';

import PanelCard from './PanelCard';
import { KpiSub, KpiValue, Trend } from '../styles';
import { UnfilteredNote } from './InsightPanels';
import {
  KPI_CODES, LIVE_INSIGHT_STATUSES, REVIEW_RULE, fetchInsights, fetchMetrics, formatNumber, hasValue, metaOfMetrics, metricOf,
  periodDiff, trendTone, usePanel, weekDiff,
} from '../config';

// Spec §6.3 — altı KPI kartı. Fark: Genel Tamamlama ve Yönetici Tamamlama karşılaştırma dönemine göre (puan), Geciken ve Kalibrasyon
// Bekleyen "son 1 haftada" (compare=7d), İnceleme Bekleyen "son 1 haftada yeni" (DetectedAt son 7 gün). Renk yönü: tamamlamada
// artış yeşil, geciken/bekleyende kırmızı; renk her zaman ok ikonu ve sayıyla birlikte.
const DAY = 24 * 60 * 60 * 1000;

function TrendLine({ diff, higherIsBetter, unit, suffix }) {
  const { t, i18n } = useTranslation();
  if (diff.reason) {
    return <Trend $tone="neutral" data-trend="reason">{t(`performance.operations.kpi.reason.${diff.reason}`, { defaultValue: t('performance.operations.kpi.reason.noSnapshot') })}</Trend>;
  }
  const tone = trendTone(diff.delta, higherIsBetter);
  const Icon = diff.delta > 0 ? ArrowUp : diff.delta < 0 ? ArrowDown : Minus;
  const sign = diff.delta > 0 ? '+' : '';
  return (
    <Trend $tone={tone} data-trend={tone}>
      <Icon size={14} aria-hidden="true" />
      <span>{`${sign}${formatNumber(diff.delta, i18n.language)}${unit === 'point' ? ` ${t('performance.operations.kpi.points')}` : ''} ${suffix}`}</span>
    </Trend>
  );
}

// Süreç Takibi (Faz 2 Plan 4) aynı kartı 4+4 düzende (span 3) kullanır; `control` başlığın sağına (ör. aşama seçimi) çizilir.
export function Kpi({
  code, title, metric, value, sub, diff, higherIsBetter, unit, suffix, loading, error, onRetry, note, span = 2, kpi = true, control,
}) {
  const { t, i18n } = useTranslation();
  const noData = !loading && !error && value === undefined && !hasValue(metric);
  const shown = value !== undefined ? value : (hasValue(metric) ? metric.value : null);
  return (
    <PanelCard panelKey={`kpi-${code}`} title={title} span={span} kpi={kpi} compact loading={loading} error={error} onRetry={onRetry} actions={control}>
      <KpiValue data-kpi={code}>
        {noData || shown == null
          ? t('performance.operations.kpi.noData')
          : `${formatNumber(shown, i18n.language)}${metric?.unit === 'percent' ? '%' : ''}`}
      </KpiValue>
      {sub ? <KpiSub>{sub}</KpiSub> : null}
      {diff ? <TrendLine diff={diff} higherIsBetter={higherIsBetter} unit={unit} suffix={suffix} /> : null}
      {note}
    </PanelCard>
  );
}

export default function KpiCards({ filters, comparePeriod, onMeta }) {
  const { t, i18n } = useTranslation();
  const periodId = filters.periodId;
  const filterKey = JSON.stringify(filters);

  const current = usePanel(() => fetchMetrics(filters, periodId, KPI_CODES, '7d'), [filterKey], !!periodId);
  const previous = usePanel(
    () => fetchMetrics(filters, comparePeriod.id, ['M02', 'pm.managerCompletion']),
    [filterKey, comparePeriod?.id],
    !!periodId && !!comparePeriod,
  );
  const review = usePanel(async () => {
    const base = { 'periodId[eq]': periodId, 'ruleCode[eq]': REVIEW_RULE, 'status[in]': LIVE_INSIGHT_STATUSES, pageNumber: 1, pageSize: 1 };
    const [all, fresh] = await Promise.all([
      fetchInsights(base),
      fetchInsights({ ...base, 'detectedAt[gte]': new Date(Date.now() - 7 * DAY).toISOString() }),
    ]);
    return { total: all.total, fresh: fresh.total };
  }, [periodId], !!periodId);

  useEffect(() => {
    if (current.data) onMeta?.('kpi', metaOfMetrics(current.data));
  }, [current.data, onMeta]);

  const m = (code) => metricOf(current.data, code);
  const p = (code) => metricOf(previous.data, code);
  const vs = comparePeriod ? t('performance.operations.kpi.vsPeriod', { name: comparePeriod.name }) : '';
  const week = t('performance.operations.kpi.lastWeek');
  const ratio = (metric) => (hasValue(metric) && metric.numerator != null && metric.denominator != null
    ? t('performance.operations.kpi.ratio', { num: formatNumber(metric.numerator, i18n.language, 0), den: formatNumber(metric.denominator, i18n.language, 0) })
    : null);
  const common = { loading: current.loading, error: current.error, onRetry: current.retry };
  const periodCompare = (code) => (previous.loading ? null : periodDiff(m(code), comparePeriod ? p(code) : null));

  return (
    <>
      <Kpi code="M01" title={t('performance.operations.kpi.coverage')} metric={m('M01')} sub={t('performance.operations.kpi.people')} {...common} />
      <Kpi
        code="M02" title={t('performance.operations.kpi.completion')} metric={m('M02')} sub={ratio(m('M02'))}
        diff={periodCompare('M02')} higherIsBetter unit="point" suffix={vs} {...common}
      />
      <Kpi
        code="M04" title={t('performance.operations.kpi.overdue')} metric={m('M04')}
        diff={current.data ? weekDiff(m('M04')) : null} higherIsBetter={false} suffix={week} {...common}
      />
      <Kpi
        code="review" title={t('performance.operations.kpi.reviewAttention')} metric={null}
        value={review.data ? review.data.total : undefined}
        diff={review.data ? { delta: review.data.fresh } : null} higherIsBetter={false} suffix={t('performance.operations.kpi.newLastWeek')}
        loading={review.loading} error={review.error} onRetry={review.retry} note={<UnfilteredNote filters={filters} />}
      />
      <Kpi
        code="pm.managerCompletion" title={t('performance.operations.kpi.managerCompletion')} metric={m('pm.managerCompletion')}
        sub={ratio(m('pm.managerCompletion'))} diff={periodCompare('pm.managerCompletion')} higherIsBetter unit="point" suffix={vs} {...common}
      />
      <Kpi
        code="pm.calibrationPending" title={t('performance.operations.kpi.calibrationPending')} metric={m('pm.calibrationPending')}
        diff={current.data ? weekDiff(m('pm.calibrationPending')) : null} higherIsBetter={false} suffix={week} {...common}
      />
    </>
  );
}
